import React from "react";
import styled from "styled-components";
import colors from "../../assets/colors";
import { mobileScreen } from "../../assets/screens";
import { FontAwesome } from "../Bootstrap";
import {
  UPLOAD_STATUS,
  type SubmissionProgress,
  type UploadStatus,
} from "../../types/submission";

export { UPLOAD_STATUS };

interface UploadSubmissionProgressProps {
  progress: SubmissionProgress | null;
  onDismiss?: () => void;
  onRetryFailed?: () => void;
  onRetrySubmit?: () => void;
  isRetrying?: boolean;
}

interface StatusIconProps {
  status: UploadStatus;
}

interface ProgressFillProps {
  percent: number;
  hasError?: boolean;
}

interface IconCircleProps {
  done?: boolean;
  error?: boolean;
}

interface ItemStatusProps {
  status: UploadStatus;
}

const StatusIcon = ({ status }: StatusIconProps) => {
  if (status === UPLOAD_STATUS.uploading) {
    return <Spinner aria-hidden="true" />;
  }
  if (status === UPLOAD_STATUS.done) {
    return (
      <IconCircle done>
        <FontAwesome fontSize={16} className="fa-solid fa-check" />
      </IconCircle>
    );
  }
  if (status === UPLOAD_STATUS.error) {
    return (
      <IconCircle error>
        <FontAwesome fontSize={16} className="fa-solid fa-xmark" />
      </IconCircle>
    );
  }
  return <IconCircle />;
};

export default function UploadSubmissionProgress({
  progress,
  onDismiss,
  onRetryFailed,
  onRetrySubmit,
  isRetrying,
}: UploadSubmissionProgressProps) {
  if (!progress) return null;

  const { items, apiStatus } = progress;
  const totalSteps = items.length + 1;
  const completedSteps =
    items.filter((i) => i.status === UPLOAD_STATUS.done).length +
    (apiStatus === UPLOAD_STATUS.done ? 1 : 0);
  const percent = Math.round((completedSteps / totalSteps) * 100);
  const failedUploadCount = items.filter(
    (i) => i.status === UPLOAD_STATUS.error
  ).length;
  const hasUploadErrors = failedUploadCount > 0;
  const hasApiError = apiStatus === UPLOAD_STATUS.error;
  const hasError = hasUploadErrors || hasApiError;
  const isActive =
    isRetrying ||
    items.some((i) => i.status === UPLOAD_STATUS.uploading) ||
    apiStatus === UPLOAD_STATUS.uploading;

  const title = isActive
    ? "Uploading your photos"
    : hasUploadErrors
    ? "Some uploads failed"
    : hasApiError
    ? "Submit failed"
    : "Uploading your photos";

  return (
    <Overlay>
      <Card>
        <Title>{title}</Title>

        <ProgressTrack>
          <ProgressFill percent={percent} hasError={hasError && !isActive} />
        </ProgressTrack>
        <ProgressLabel>
          {completedSteps} of {totalSteps} complete
        </ProgressLabel>

        <ItemList>
          {items.map((item) => (
            <Item key={item.alias}>
              <StatusIcon status={item.status} />
              <ItemName>{item.view}</ItemName>
              <ItemStatus status={item.status}>
                {item.status === UPLOAD_STATUS.uploading && "Uploading…"}
                {item.status === UPLOAD_STATUS.done && "Done"}
                {item.status === UPLOAD_STATUS.error && "Failed"}
                {item.status === UPLOAD_STATUS.pending && "Waiting"}
              </ItemStatus>
            </Item>
          ))}

          <Item>
            <StatusIcon status={apiStatus} />
            <ItemName>Sending capture data</ItemName>
            <ItemStatus status={apiStatus}>
              {apiStatus === UPLOAD_STATUS.uploading && "Submitting…"}
              {apiStatus === UPLOAD_STATUS.done && "Done"}
              {apiStatus === UPLOAD_STATUS.error && "Failed"}
              {apiStatus === UPLOAD_STATUS.pending && "Waiting"}
            </ItemStatus>
          </Item>
        </ItemList>

        {!isActive && hasUploadErrors && onRetryFailed && (
          <RetryBtn type="button" onClick={onRetryFailed}>
            Retry failed uploads ({failedUploadCount})
          </RetryBtn>
        )}

        {!isActive && hasApiError && !hasUploadErrors && onRetrySubmit && (
          <RetryBtn type="button" onClick={onRetrySubmit}>
            Retry submit
          </RetryBtn>
        )}

        {!isActive && hasError && onDismiss && (
          <DismissBtn type="button" onClick={onDismiss}>
            Close
          </DismissBtn>
        )}
      </Card>
    </Overlay>
  );
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  padding: 20px;
`;

const Card = styled.div`
  background: ${colors.white};
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 400px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);

  ${mobileScreen(`
    padding: 20px 16px;
  `)}
`;

const Title = styled.h3`
  margin: 0 0 16px;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.black};
  text-align: center;
`;

const ProgressTrack = styled.div`
  height: 8px;
  background: ${colors.grey2};
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div<ProgressFillProps>`
  height: 100%;
  width: ${(props) => props.percent}%;
  background: ${(props) => (props.hasError ? "#e74c3c" : colors.blue3)};
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const ProgressLabel = styled.p`
  margin: 8px 0 16px;
  font-size: 13px;
  color: ${colors.grey};
  text-align: center;
`;

const ItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Item = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid ${colors.grey2};

  &:last-child {
    border-bottom: none;
  }
`;

const IconCircle = styled.span<IconCircleProps>`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  background: ${(props) =>
    props.done ? colors.green : props.error ? "#e74c3c" : colors.grey2};
  color: ${(props) => (props.done || props.error ? colors.white : "transparent")};
`;

const Spinner = styled.span`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border: 2px solid ${colors.grey2};
  border-top-color: ${colors.blue3};
  border-radius: 50%;
  animation: spin 0.7s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ItemName = styled.span`
  flex: 1;
  font-size: 14px;
  text-transform: capitalize;
  color: ${colors.black};
`;

const ItemStatus = styled.span<ItemStatusProps>`
  font-size: 12px;
  color: ${(props) =>
    props.status === UPLOAD_STATUS.done
      ? colors.green
      : props.status === UPLOAD_STATUS.error
      ? "#e74c3c"
      : props.status === UPLOAD_STATUS.uploading
      ? colors.blue3
      : colors.grey};
  font-weight: 500;
  min-width: 72px;
  text-align: right;
`;

const DismissBtn = styled.button`
  display: block;
  width: 100%;
  margin-top: 12px;
  padding: 12px;
  border: none;
  border-radius: 8px;
  background: ${colors.grey2};
  color: ${colors.black};
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
`;

const RetryBtn = styled.button`
  display: block;
  width: 100%;
  margin-top: 16px;
  padding: 12px;
  border: none;
  border-radius: 8px;
  background: ${colors.blue3};
  color: ${colors.white};
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
`;
